import jwt_decode from 'jwt-decode'
import { AccessToken, CollectionApiParams, PaginationRequest, SortingRequest, ToQueryParamsObject } from '~/types'
import { localStore, StoreKey } from './local-store'

interface FormatNumberToCurrencyStringOption {
    roundType: 'round' | 'ceil' | 'floor'
    round: boolean
}

export const formatNumberToCurrencyString = (
    num = 0,
    options: FormatNumberToCurrencyStringOption = {
        roundType: 'round',
        round: true
    }
) => {
    const { roundType, round } = options
    let value = num

    if (round) {
        value = Math[roundType]((num + Number.EPSILON) * 100) / 100
    }

    return String(value)
        .replace(/\./g, '.') // Keep the decimal as period
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,') // Use comma for thousands separator
}

export const isNumberOnly = (number: string) => {
    const numberRegx = /^\d+$/
    return number.match(numberRegx)
}

export const delayedCallback = (callback: () => void, timeout = 150) => {
    setTimeout(() => {
        callback()
    }, timeout)
}

export const padTime = (time: number) => {
    return String(time).length === 1 ? `0${time}` : `${time}`
}

export const formatTime = (time: number) => {
    // Convert seconds into minutes and take the whole part
    const minutes = Math.floor(time / 60)

    // Get the seconds left after converting minutes
    const seconds = time % 60

    //Return combined values as string in format mm:ss
    return `${minutes}:${padTime(seconds)}`
}

export const convertToNumber = (value: string) => {
    return Number(value.replace(/\D/g, '').replace(/\b0+/g, ''))
}
export const convertToNumberDecimal = (value: string) => {
    if (value === '') return 0
    // Allow only digits and one decimal point
    const cleanedValue = value.replace(/[^0-9.]/g, '') // Keep digits and decimal point
    const parts = cleanedValue.split('.')

    if (parts.length > 2) {
        // If more than one dot, take only the first two parts
        const integerPart = parts[0]
        const decimalPart = parts[1].slice(0, 2)
        return parseFloat(`${integerPart}.${decimalPart}`)
    }

    const integerPart = parts[0] || '0'
    const decimalPart = parts[1] ? parts[1].slice(0, 2) : ''

    // Reassemble the number with truncated decimal places
    const finalValue = decimalPart ? `${integerPart}.${decimalPart}` : integerPart

    return parseFloat(finalValue) // Convert to number and return
}

export const SkuIDGenerator = () => {
    const random = Math.floor(1000000000 + Math.random() * 9000000000).toString()
    return '123' + random
}

export const SkuIDGeneratorForTranferOrder = () => {
    const random = Math.floor(10000 + Math.random() * 90000).toString() // Generates a 5-digit random number
    return `TO-${random}`
}

export const GetCustomerShortNames = (name: string) => {
    if (!name) {
        return '-'
    }

    return name
        .split(/\s/)
        .reduce((response, word) => (response += word.slice(0, 1)), '')
        .toUpperCase()
        .substring(0, 3)
}

export const getCurrentBusiness = () => {
    const business = localStore.get(StoreKey.SELECTED_BUSINESS)?.data

    if (!business) {
        return null
    }

    return business
}

export const getCurrentBusinessData = () => {
    const business = localStore.get(StoreKey.SELECTED_BUSINESS)

    if (!business) {
        return null
    }

    return business
}

export const setCurrentBusiness = (businessId: number) => {
    localStore.set(StoreKey.SELECTED_BUSINESS, businessId)
}

export const unsetCurrentBusiness = () => {
    localStore.remove(StoreKey.SELECTED_BUSINESS)
}

export const getAccessToken = (token?: string): AccessToken | null => {
    const accessToken = !token ? localStore.get(StoreKey.ACCESS_TOKEN)?.data : token
    try {
        return jwt_decode(accessToken!)
    } catch {
        return null
    }
}

export const difference = (a: number, b: number) => {
    return Math.abs(a - b)
}

export const isElementInViewport = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect()

    return isInViewport(rect.top, rect.right, rect.bottom, rect.left)
}

export const isInViewport = (_top: number, _right: number, _bottom: number, _left: number) => {
    const top = _top >= 0
    const right = _right <= (window.innerWidth || document.documentElement.clientWidth)
    const bottom = _bottom <= (window.innerHeight || document.documentElement.clientHeight)
    const left = _left >= 0

    return {
        top,
        right,
        bottom,
        left,
        all: top && left && bottom && right
    }
}

type MultiPromiseResolver = <T extends any[]>(promises: { [K in keyof T]: Promise<T[K]> }) => Promise<{
    [K in keyof T]: { status: 'fulfilled' | 'rejected'; value?: T[K]; reason?: any }
}>

export const multiPromiseResolver: MultiPromiseResolver = <T extends any[]>(promises: {
    [K in keyof T]: Promise<T[K]>
}) => {
    return Promise.all(
        promises.map((promise) =>
            promise
                .then((value) => ({ status: 'fulfilled' as const, value }))
                .catch((reason) => ({ status: 'rejected' as const, reason }))
        )
    ) as Promise<{ [K in keyof T]: { status: 'fulfilled' | 'rejected'; value?: T[K]; reason?: any } }>
}

export const replaceToPhoneNumberFormat = (text: string) => {
    return text.replace(/[^0-9+]/g, '').replace(/\b0+/g, '')
}

export const countryCodeRegex = /^(?!91)/g

export const generateRandomSkuHash = () => {
    const length = Math.floor(Math.random() * 3) + 6 // Random length between 6 and 8
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const numbers = '0123456789'
    let result = ''
    // Start with 2 characters
    for (let i = 0; i < 2; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    // Followed by numbers
    for (let i = 2; i < length; i++) {
        result += numbers.charAt(Math.floor(Math.random() * numbers.length))
    }
    return result
}

export const randomNumber = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min
}

export const convertToQueryParams = <T extends object, M extends ToQueryParamsObject<CollectionApiParams<T>>>(
    params: CollectionApiParams<T>,
    callback: (queryString: string, queryObject: M) => void,
    skippedKeys: (keyof CollectionApiParams<T>)[] = []
) => {
    const query = new URLSearchParams()
    const queryObject = {} as M

    Object.keys(params).forEach((key) => {
        const typedKey = key as keyof CollectionApiParams<T>
        const queryValue = params[typedKey]
        if (skippedKeys.includes(typedKey)) {
            return
        }
        if (queryValue) {
            query.append(key, queryValue.toString())
            queryObject[typedKey] = queryValue.toString() as unknown as M[keyof CollectionApiParams<T>]
        } else if (queryValue === false) {
            query.append(key, 'false')
        }
    })
    const queryString = query.toString()
    callback(queryString, queryObject)
    return queryString
}
export const defaultPagination: PaginationRequest = {
    offset: 0,
    limit: 25
}
export const defaultSort: SortingRequest = {
    sortBy: 'name',
    orderBy: 'DESC'
}


export function formatAmount(amount: number | null | undefined, currencySymbol?: string, currencyCodeArg?: string): string {
    const currencyLocaleMap: Record<string, string> = {
        'USD': 'en-US',
        'EUR': 'de-DE',
        'GBP': 'en-GB',
        'INR': 'en-IN',
        'JPY': 'ja-JP',
        'AUD': 'en-AU',
        'CAD': 'en-CA',
        'CHF': 'de-CH',
        'AED': 'ar-AE',
        'ZAR': 'en-ZA',
        'THB': 'th-TH',
        'KES': 'en-KE',
        'KRW': 'ko-KR',
        'IDR': 'id-ID',
        'MYR': 'ms-MY',
        'VND': 'vi-VN',
        'TRY': 'tr-TR',
        'PHP': 'en-PH',
        'AFN': 'ps-AF',
        'ALL': 'sq-AL',
        'AMD': 'hy-AM',
        'ANG': 'nl-CW',
        'AOA': 'pt-AO',
        'ARS': 'es-AR',
        'AWG': 'nl-AW',
        'AZN': 'az-AZ',
        'BAM': 'bs-BA',
        'BBD': 'en-BB',
        'BDT': 'bn-BD',
        'BGN': 'bg-BG',
        'BHD': 'ar-BH',
        'BIF': 'fr-BI',
        'BMD': 'en-BM',
        'BND': 'ms-BN',
        'BOB': 'es-BO',
        'BRL': 'pt-BR',
        'BSD': 'en-BS',
        'BTN': 'dz-BT',
        'BWP': 'en-BW',
        'BYN': 'be-BY',
        'BZD': 'en-BZ',
        'CLP': 'es-CL',
        'COP': 'es-CO',
        'CRC': 'es-CR',
        'CUP': 'es-CU',
        'CVE': 'pt-CV',
        'CZK': 'cs-CZ',
        'DOP': 'es-DO',
        'DZD': 'ar-DZ',
        'ERN': 'ti-ER',
        'FJD': 'en-FJ',
        'GEL': 'ka-GE',
        'GHS': 'en-GH',
        'GTQ': 'es-GT',
        'GYD': 'en-GY',
        'HKD': 'zh-HK',
        'HUF': 'hu-HU',
        'ILS': 'he-IL',
        'IQD': 'ar-IQ',
        'IRR': 'fa-IR',
        'ISK': 'is-IS',
        'JMD': 'en-JM',
        'JOD': 'ar-JO',
        'KGS': 'ky-KG',
        'KHR': 'km-KH',
        'KWD': 'ar-KW',
        'KZT': 'kk-KZ',
        'LAK': 'lo-LA',
        'LBP': 'ar-LB',
        'MAD': 'ar-MA',
        'MKD': 'mk-MK',
        'MMK': 'my-MM',
        'MNT': 'mn-MN',
        'MUR': 'en-MU',
        'MXN': 'es-MX',
        'MZN': 'pt-MZ',
        'NAD': 'en-NA',
        'NGN': 'en-NG',
        'NZD': 'en-NZ',
        'PEN': 'es-PE',
        'PLN': 'pl-PL',
        'PYG': 'es-PY',
        'RON': 'ro-RO',
        'RSD': 'sr-RS',
        'RUB': 'ru-RU',
        'XCD': 'en-AG',
        'TTD': 'en-TT',
        'UAH': 'uk-UA',
        'UYU': 'es-UY',
        'UZS': 'uz-UZ',
        'WST': 'sm-WS',
        'ZMW': 'en-ZM',
        'CNY': 'zh-CN',
        'EGP': 'en-EG',
        'SGD': 'en-SG',
        'SEK': 'sv-SE',
        'NOK': 'nb-NO',
        'DKK': 'da-DK',
        'SAR': 'en-SA',
        'QAR': 'ar-QA',
        'OMR': 'ar-OM',
        'PKR': 'ur-PK',
        'LKR': 'si-LK',
        'NPR': 'ne-NP',
        'UGX': 'en-UG',
        'CDF': 'fr-CD',
        'DJF': 'fr-DJ',
        'ETB': 'am-ET',
        'GMD': 'en-GM',
        'GNF': 'fr-GN',
        'HNL': 'es-HN',
        'HTG': 'fr-HT',
        'KMF': 'fr-KM',
        'LRD': 'en-LR',
        'MGA': 'fr-MG',
        'NIO': 'es-NI',
        'RWF': 'rw-RW',
        'SDG': 'ar-SD',
        'SLL': 'en-SL',
        'SZL': 'en-SZ',
        'TJS': 'tg-TJ',
        'TMT': 'tk-TM',
        'TND': 'ar-TN',
        'TZS': 'sw-TZ',
        'VES': 'es-VE',
        'XAF': 'fr-CM',
        'XOF': 'fr-SN',
        'XPF': 'fr-PF',
        'YER': 'ar-YE'
    };

    let currencyCode = currencyCodeArg || "USD";
    if (typeof window !== 'undefined' && (window as any).__INVOICE_CURRENCY_CODE_OVERRIDE__) {
        currencyCode = (window as any).__INVOICE_CURRENCY_CODE_OVERRIDE__;
    }
    const value = amount ?? 0;
    const effectiveLocale = currencyLocaleMap[currencyCode] || 'en-IN';

    try {
        // Use 'decimal' style to get locale-specific formatting (commas/dots)
        // without the automatic ISO symbol, allowing us to use the custom symbol.
        const formattedNumber = new Intl.NumberFormat(effectiveLocale, {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);

        return currencySymbol ? `${currencySymbol} ${formattedNumber}` : formattedNumber;
    } catch (e) {
        const fallbackValue = value.toLocaleString(effectiveLocale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return currencySymbol ? `${currencySymbol} ${fallbackValue}` : fallbackValue;
    }
}

/**
 * Common function to check if a transaction will result in negative stock.
 * If disabled and stock goes negative, it shows a toast error.
 * 
 * @returns boolean - true if blocked (negative), false otherwise.
 */
export const handleNegativeStockCheck = ({
    quantity,
    currentStock,
    isNegativeDisabled,
    setToastProps,
    toastProps,
    localeText
}: {
    quantity: number
    currentStock: number
    isNegativeDisabled: boolean
    setToastProps: (props: any) => void
    toastProps: any
    localeText: any
}) => {
    if (isNegativeDisabled && currentStock - quantity < 0) {
        setToastProps({
            ...toastProps,
            type: 'vibrant-error',
            message: localeText?.business?.settings?.negativeStockError || 'Negative stock transactions are disabled',
            open: true,
            position: 'bottom-right'
        })
        return true // Blocked
    }
    return false // Allowed
}

export const base64ToFile = (base64: string, filename: string) => {
    const arr = base64.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1]
    const bstr = atob(arr[1])
    let n = bstr.length
    const u8arr = new Uint8Array(n)
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
    }
    return new File([u8arr], filename, { type: mime })
}
